import * as THREE from "three";
import { PROP_KINDS, type PropBody, type PropKind } from "../props";
import { disposeWorld } from "./level-meshes";
import { inked, toon } from "./toon";

// The map's cones and cans (#151, `props.ts`): a traffic cone (orange, a
// white band, a square foot) and an empty paint can, each sized to its
// kind. Standing until the power washer's jet knocks it over; then it lies
// on its side along the way it was last pushed, and a can turns over as it
// rolls. Where each is comes from the skater's `PackState.props` every frame.

/** How quickly a knocked prop tips over onto its side (1/s). */
const TIP_RATE = 9;

/** How far round a knocked cone spins for each metre it skids (rad). */
const SKID_SPIN = 0.9;

// Each is built round its middle, its axis up y, so it tips and rolls about that.

function cone(): THREE.Group {
  const { r, h } = PROP_KINDS.cone;
  const g = new THREE.Group();
  const base = inked(new THREE.BoxGeometry(r * 2.2, 0.03, r * 2.2), toon("#2b2b2b", { roughness: 0.7 }, "plastic"), 0.008);
  base.position.y = 0.015 - h / 2;
  const body = inked(new THREE.ConeGeometry(r, h - 0.03, 16, 1, true), toon("#ff6a1a", { roughness: 0.5 }, "plastic"), 0.01);
  body.position.y = 0.03 + (h - 0.03) / 2 - h / 2;
  const band = inked(new THREE.CylinderGeometry(r * 0.52, r * 0.68, 0.08, 16, 1, true), toon("#f4f4f4", { roughness: 0.4 }, "plastic"), 0);
  band.position.y = 0.03 + (h - 0.03) * 0.45 - h / 2;
  g.add(base, body, band);
  return g;
}

function can(): THREE.Group {
  const { r, h } = PROP_KINDS.can;
  const g = new THREE.Group();
  const body = inked(new THREE.CylinderGeometry(r, r, h, 16), toon("#c8ccd4", { roughness: 0.35 }, "metal"), 0.008);
  const label = inked(new THREE.CylinderGeometry(r * 1.01, r * 1.01, h * 0.55, 16, 1, true), toon("#ff3b6b", { roughness: 0.5 }, "paintedMetal"), 0);
  g.add(body, label);
  return g;
}

const BUILD: Record<PropKind, () => THREE.Group> = { cone, can };

interface PropMesh {
  /** Placed and turned to face where it's heading. */
  root: THREE.Group;
  /** Tipped over about the root's x axis... */
  tip: THREE.Group;
  /** ...and turned about the prop's own (a can rolling). */
  roll: THREE.Group;
  /** 0 standing, 1 on its side. */
  over: number;
}

export class PropMeshes {
  readonly root = new THREE.Group();
  private items: PropMesh[];

  constructor(props: readonly PropBody[]) {
    this.root.name = "props";
    this.items = props.map((p) => {
      const root = new THREE.Group();
      const tip = new THREE.Group();
      const roll = new THREE.Group();
      roll.add(BUILD[p.kind]());
      tip.add(roll);
      root.add(tip);
      this.root.add(root);
      return { root, tip, roll, over: 0 };
    });
  }

  /** Puts every prop where the sim has it; one just knocked over tips onto its side. */
  update(props: readonly PropBody[], dt: number): void {
    this.items.forEach((m, i) => {
      const p = props[i];
      if (!p) return;
      m.root.visible = !p.sunk;
      m.over = p.down ? Math.min(1, m.over + dt * TIP_RATE) : 0;
      const { r, h } = PROP_KINDS[p.kind];
      // Its middle: half its height up standing, its radius up on its side (a cone's slant sits it a bit lower).
      const lie = p.kind === "can" ? r : r * 0.6;
      m.root.position.set(p.x, p.y + h / 2 + (lie - h / 2) * m.over, p.z);
      // A can lies across the way it rolls, turning over as it goes; a cone
      // goes over point first and spins round flat as it skids.
      m.root.rotation.y = p.kind === "can" ? p.yaw + Math.PI / 2 : p.yaw + p.dist * SKID_SPIN;
      m.tip.rotation.x = (Math.PI / 2) * m.over;
      m.roll.rotation.y = p.kind === "can" ? p.dist / r : 0;
    });
  }

  dispose(): void {
    disposeWorld(this.root);
  }
}
